import { ApiError, ApiException } from '@/utils/errorHandler';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://www.getpoints.com.br';

async function apiFetch<T>(method: string, endpoint: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    const json = await res.json().catch(() => null);

    if (!res.ok) {
      const errorPayload: ApiError = {
        timestamp: json?.timestamp || new Date().toISOString(),
        status: json?.status || res.status,
        error: json?.error || 'Unexpected Error',
        message: json?.message || 'Erro inesperado',
        path: json?.path || endpoint,
        traceId: json?.traceId || 'unknown',
        details: json?.details || [],
      };
      throw new ApiException(errorPayload);
    }

    return json as T;
  } catch (err) {
    // Re-throw ApiException as-is
    if (err instanceof ApiException) {
      throw err;
    }

    // Convert network errors to ApiException
    if (err instanceof Error) {
      const errorPayload: ApiError = {
        timestamp: new Date().toISOString(),
        status: 0,
        error: 'Network Error',
        message: err.message === 'Failed to fetch' || err.message === 'NetworkError when attempting to fetch resource.'
          ? 'Servidor indisponível no momento. Verifique sua conexão e tente novamente.'
          : err.message,
        path: endpoint,
        traceId: 'unknown',
        details: ['Erro de conexão. Verifique se o servidor está online ou se há problemas de rede.'],
      };
      throw new ApiException(errorPayload);
    }

    throw err;
  }
}

// ─── Login ───
export interface LoginPayload {
  login: string;
  password: string;
  deviceFingerprint: string;
}

export interface LoginSuccess {
  accessToken: string;
  refreshToken: string;
  sessionId: string;
  expiresIn: number;
  tokenType: string;
  riskLevel: string;
}

export async function login(payload: LoginPayload): Promise<LoginSuccess> {
  return apiFetch<LoginSuccess>('POST', '/api/v1/auth/login', payload);
}

// ─── MFA Validate ───
export interface MfaPayload {
  challengeId: string;
  otpCode: string;
  deviceFingerprint: string;
}

export async function validateMfa(payload: MfaPayload): Promise<LoginSuccess> {
  return apiFetch<LoginSuccess>('POST', '/api/v1/auth/mfa/validate', payload);
}